import * as React from "react";
import classNames from "classnames";
import * as moment from "moment";
import { Subscription } from "rxjs";
import { WithStyles, withStyles, createStyles, Theme, StyleRulesCallback } from '../mui/styles';
import DataSource, { DataSourceState, SortModel, SortDirection } from "../../data/DataSource";
import { Column, HeaderColumn, BodyColumn, DataDridCellRenderFunc, DataGridLines, DataGridPadding } from "./Types";
import DataGridSpinner from "./DataGridSpinner";
import DataGridHeader, { DataGridHeaderProps } from "./DataGridHeader";
import DataGridBody, { DataGridBodyProps } from "./DataGridBody";
import DataGridPagination, { DataGridPaginationProps, PaginationSettings } from "./DataGridPagination";
import { isDefined } from "../../utils";
import "../../formatting/formatMoney";
import fmtNum from "../../formatting/formatNumber";


export interface DataGridProps<T> {
    dataSource: DataSource<T>;
    columns: Column<T>[];

    allowResizing?: boolean;
    allowSorting?: boolean;
    allowFiltering?: boolean;
    allowPaging?: boolean;


    paginationSettings?: PaginationSettings;
    //    width?: number;
    //    minWidth?: number;
    height?: 'grow' | number,
    //    minHeight?: number;


    gridLines?: DataGridLines;
    padding?: DataGridPadding;

    showBorder?: boolean;
    noItemsContent?: React.ReactNode;
    onClickRow?: (row: T) => void;
}

interface State<T> {
    dsState: DataSourceState<T>;
    loading: boolean;
}

declare type Props<T> = DataGridProps<T> & WithStyles<typeof styles>;

class DataGrid<T> extends React.PureComponent<Props<T>, State<T>> {

    constructor(props: Props<T>) {
        super(props);

        const { dataSource, allowPaging } = this.props;

        this.state = {
            dsState: dataSource.getState(),
            loading: true,
        }

    }

    private subscription: Subscription;
    private headerRefObject: React.RefObject<HTMLDivElement> = React.createRef();


    refresh = () => {
    }


    private getColumnContent = (col: Column<T>, row: any): React.ReactNode => {

        const fields = col.field.split('.');
        let value = row[fields[0]];
        for(let i = 1; i < fields.length; i++) {
            value = value[fields[i]];
        }

        // const value = row[col.field];

        let aa: number;
        //aa.toExponential

        if (isDefined(col.render)) {
            return col.render(col, row);
        }

        if (col.format === undefined) {
            return isDefined(value) ? value.toString() : undefined;
        }
        else if (typeof col.format === "string") {
            switch (col.type) {
                case 'date': return isDefined(value) ? moment(value).format(col.format) : null;
                case 'number':
                    // console.log((value as Number).formatMoney(2));
                    return isDefined(value) ? fmtNum(col.format, value as number) : null;
            }
        }
        else {
            return (col.format as DataDridCellRenderFunc<T>)(col, row);
        }
    }


    private getDataColumns = (): BodyColumn<T>[] => {

        const { columns } = this.props;
        let result: BodyColumn<T>[] = [];
        columns.map(c => {
            if (c.field) {
                result.push({
                    content: (row: any) => this.getColumnContent(c, row)
                });
            }
        });

        return result;
    }


    private getAllowSorting = (col: Column<T>): boolean => {
        return col.allowSorting !== false && col.field !== undefined;
    }

    private getSortDirection = (col: Column<T>, sortedItems: { [index: string]: SortModel }): SortDirection => {
        if (col.field !== undefined) {
            return sortedItems[col.field] ? sortedItems[col.field].direction : undefined;
        }
        return null;
    }
    private getHeaderRows = () => {

        // const getTableHeaderProps = function <T>(props: DataTableProps<T>, sortedItems: { [index: string]: SortModel }, filteredItems: { [index: string]: FilterModel }, onSort: (sort: SortModel) => void, onFilter: (field: string, value: string) => void): DataTableHeaderProps {
        // (this.props, this.state.dsState.sortedItems, this.state.dsState.filteredItems, this.dataSource.onSort, this.dataSource.onFilter);

        const { columns, allowSorting, allowFiltering, dataSource } = this.props;
        const { sortedItems, filteredItems } = this.state.dsState;
        const { onSort, onFilter } = dataSource;
        let headerRows: HeaderColumn[][] = [];
        let index = 0;
        columns.map(c => {
            const row = 0;

            if (headerRows.length <= row) {
                headerRows.push([]);
            }

            let sorting: boolean = (allowSorting === true) && this.getAllowSorting(c);
            let isSorted: boolean = allowSorting && sortedItems[c.field] !== undefined;
            let sortDirection: SortDirection = this.getSortDirection(c, sortedItems);

            let filtering: boolean = (allowFiltering === true && c.allowFiltering !== false);
            let isFiltered: boolean = allowFiltering && filteredItems[c.field] !== undefined;
            let filterOperator = filteredItems[c.field] ? filteredItems[c.field].operator : null;

            const rowSpan = 1;
            const colSpan = 1;

            const col: HeaderColumn = {
                index: index,
                field: c.field,
                rowSpan: rowSpan,
                colSpan: colSpan,
                width: c.width,
                title: c.headerText,
                allowSorting: sorting,
                sortDirection: sortDirection,
                isSorted: isSorted,
                onSort: onSort,
                allowFiltering: filtering,
                isFiltered: isFiltered,
                filterOperator: filterOperator,
                //onFilter: onFilter
            }

            headerRows[row].push(col);

            index++;
        });

        return headerRows;
    }

    componentDidMount() {
        const { dataSource } = this.props;
        this.subscription = dataSource.state$.subscribe(state => this.setState({ dsState: state, loading: state.loading }));
        this.setState({
            dsState: dataSource.getState(),
            loading: true
        });
        dataSource.fetch();
    }

    componentWillUnmount() {
        this.subscription && this.subscription.unsubscribe();
        this.subscription = undefined;
    }

    render() {

        const { classes, children, height, allowPaging, padding, gridLines, dataSource, paginationSettings, showBorder, noItemsContent, onClickRow } = this.props;
        const { loading } = this.state;
        const fixedHeight = height !== undefined;


        const rootClass = classNames({
            [classes.root]: true,
            [classes.growHeight]: height === 'grow',
            [classes.border]: showBorder !== false,
        });

        const rootStyle: React.CSSProperties = {
            //height: 300
        };

        if (height) {
            rootStyle.height = height;
        }

        const headerRows = this.getHeaderRows();
        const headerProps: DataGridHeaderProps = {
            headerRows: headerRows,
            headerRef: this.headerRefObject,
            //   minWidth: this.props.minWidth,
            fixedHeight: fixedHeight,
            gridLines: gridLines,
            padding: padding,
        }

        const bodyProps: DataGridBodyProps<T> = {
            columns: this.getDataColumns(),
            headerRows: headerRows,
            rows: this.state.dsState.data,
            loading: this.state.dsState.loading,
            hasError: this.state.dsState.hasError,
            errorMsg: this.state.dsState.errorMsg,
            scrollX: (x: number) => {
                // console.log("SCROLL: ", x);
                // console.log("SCROLL: ", this.headerRefObject.current, this.headerRefObject.current.scrollTo);
                this.headerRefObject.current.scrollTo(x, 0);
            },
            fixedHeight: fixedHeight,
            //  minWidth: this.props.minWidth,
            // height: 150
            gridLines: gridLines,
            padding: padding,
            onClickRow: onClickRow,
        }

        const paginationProps: DataGridPaginationProps<T> = {
            dataSource: dataSource,
            dataSourceState: this.state.dsState,
            paginationSettings: paginationSettings,
            allowPaging: allowPaging,
        }

        return <div className={rootClass} style={rootStyle} >
            <DataGridHeader {...headerProps} />
            <DataGridBody {...bodyProps} noItemsContent={noItemsContent} />
            <DataGridPagination {...paginationProps} />
            <DataGridSpinner loading={loading} />
        </div>
    }
}


const styles = ((theme: Theme) => createStyles({
    root: {
        //            border: '1px solid rgba(224, 224, 224, 1)',

        //width: this.props.width,
        //width: 'auto',
        width: '100%',
        height: 'min-content',
        //  width: width || '100%',
        //  height: height || (growHeight ? 'auto' : 'min-content'),
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
    },
    border: {
        border: '1px solid ' + theme.palette.divider,
    },
    growHeight: {
        height: 'auto',
        flexGrow: 1,
    },
})) as StyleRulesCallback;

export default withStyles(styles)(DataGrid);
